import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { API, DEFAULT_DEPARTMENTS, sectorsCache, updatePageTitle } from '../lib/appConfig';
import { useTheme } from '../hooks/useTheme';

// Survey Flow Component with theme support
export const SurveyFlow = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState('welcome');
  const [sessionId, setSessionId] = useState(null);
  const [tag, setTag] = useState(searchParams.get('tag') || searchParams.get('cliente') || '');
  const [demographics, setDemographics] = useState({
    department: '',
    position: '',
    company_time: ''
  });
  const [responses, setResponses] = useState({});
  const [currentDimension, setCurrentDimension] = useState(0);
  const [questions, setQuestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sectorOptions, setSectorOptions] = useState(null); // null = ainda não carregado
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [sectorsError, setSectorsError] = useState(false);

  // Persistência da sessão em andamento (sobrevive a F5/recarregamento)
  const STORAGE_KEY = 'survey_session_v1';

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.sessionId && data.currentStep && !['complete', 'limit_exceeded', 'client_not_found'].includes(data.currentStep)) {
          setSessionId(data.sessionId);
          setCurrentStep(data.currentStep);
          setDemographics(data.demographics || { department: '', position: '', company_time: '' });
          setResponses(data.responses || {});
          setCurrentDimension(data.currentDimension || 0);
          if (data.tag) setTag(data.tag);
        }
      }
    } catch (e) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    // Executa apenas uma vez na montagem (restauração de sessão salva)
  }, []);

  useEffect(() => {
    if (!sessionId || ['complete', 'limit_exceeded', 'client_not_found'].includes(currentStep)) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        sessionId, currentStep, demographics, responses, currentDimension, tag
      }));
    } catch (e) { /* armazenamento indisponível — segue sem persistir */ }
  }, [sessionId, currentStep, demographics, responses, currentDimension, tag]);

  // Aviso ao tentar sair/recarregar com pesquisa em andamento
  useEffect(() => {
    const handler = (e) => {
      if (currentStep === 'demographics' || currentStep === 'questions') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [currentStep]);

  const dimensionNames = ['demandas', 'controle', 'apoio', 'reconhecimento', 'seguranca', 'desenvolvimento', 'saude', 'ofensivos'];
  const dimensionLabels = {
    demandas: 'Demandas de Trabalho',
    controle: 'Controle e Autonomia',
    apoio: 'Apoio Social e Comunicação',
    reconhecimento: 'Reconhecimento e Justiça',
    seguranca: 'Segurança no Emprego',
    desenvolvimento: 'Desenvolvimento e Significado',
    saude: 'Saúde e Bem-estar',
    ofensivos: 'Comportamentos Ofensivos'
  };

  // Scale options with emojis and colors
  const scaleOptions = [
    { value: 1, label: 'Discordo Totalmente', emoji: '😞', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-500' },
    { value: 2, label: 'Discordo Parcialmente', emoji: '🙁', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50', borderColor: 'border-orange-500' },
    { value: 3, label: 'Neutro', emoji: '😐', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-500' },
    { value: 4, label: 'Concordo Parcialmente', emoji: '🙂', color: 'bg-lime-500', textColor: 'text-lime-700', bgColor: 'bg-lime-50', borderColor: 'border-lime-500' },
    { value: 5, label: 'Concordo Totalmente', emoji: '😊', color: 'bg-demoraes-green', textColor: 'text-green-700', bgColor: 'bg-green-50', borderColor: 'border-demoraes-green' }
  ];

  // Inverted scale options for negative polarity questions (emojis reflect the actual impact)
  const invertedScaleOptions = [
    { value: 1, label: 'Discordo Totalmente', emoji: '😊', color: 'bg-demoraes-green', textColor: 'text-green-700', bgColor: 'bg-green-50', borderColor: 'border-demoraes-green' },
    { value: 2, label: 'Discordo Parcialmente', emoji: '🙂', color: 'bg-lime-500', textColor: 'text-lime-700', bgColor: 'bg-lime-50', borderColor: 'border-lime-500' },
    { value: 3, label: 'Neutro', emoji: '😐', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-500' },
    { value: 4, label: 'Concordo Parcialmente', emoji: '🙁', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50', borderColor: 'border-orange-500' },
    { value: 5, label: 'Concordo Totalmente', emoji: '😞', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-500' }
  ];

  // Function to get the appropriate scale based on question polarity
  const getScaleForQuestion = (currentQuestion) => {
    if (!currentQuestion) return scaleOptions;
    
    // Check if current question has reverse polarity
    const reversePolarity = currentQuestion.reverse_polarity || false;
    return reversePolarity ? invertedScaleOptions : scaleOptions;
  };

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    updatePageTitle();
    const urlTag = searchParams.get('tag') || searchParams.get('cliente') || '';
    if (urlTag) {
      setTag(urlTag);
    }
  }, [searchParams]);

  // Busca dinâmica dos setores cadastrados para a empresa (tag) da sessão.
  // Se a empresa não tiver setores (nem cadastrados manualmente, nem
  // migrados do histórico) ou a busca falhar, cai para a lista estática
  // padrão (DEFAULT_DEPARTMENTS), preservando o comportamento anterior.
  const fetchSectors = useCallback(async (currentTag) => {
    if (!currentTag) {
      setSectorOptions(DEFAULT_DEPARTMENTS);
      return;
    }
    if (sectorsCache[currentTag]) {
      setSectorOptions(sectorsCache[currentTag]);
      return;
    }
    setSectorsLoading(true);
    setSectorsError(false);
    try {
      const response = await axios.get(`${API}/sectors`, { params: { tag: currentTag } });
      const fetched = response.data?.sectors || [];
      const options = fetched.length > 0
        ? fetched.map((s) => ({ value: s.name, label: s.name }))
        : DEFAULT_DEPARTMENTS;
      sectorsCache[currentTag] = options;
      setSectorOptions(options);
    } catch (error) {
      console.error('Error fetching sectors:', error);
      setSectorsError(true);
      setSectorOptions(DEFAULT_DEPARTMENTS);
    }
    setSectorsLoading(false);
  }, []);

  useEffect(() => {
    if (currentStep === 'demographics' && sectorOptions === null) {
      fetchSectors(tag);
    }
  }, [currentStep, tag, sectorOptions, fetchSectors]);

  const startSession = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/start`, { tag });
      setSessionId(response.data.session_id);
      setCurrentStep('demographics');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error starting session:', error);
      
      // Check if it's a limit exceeded error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'limit_exceeded') {
        const errorData = error.response.data.detail;
        setCurrentStep('limit_exceeded');
        setResult({
          error: true,
          type: 'limit_exceeded',
          message: errorData.message,
          client_name: errorData.client_name,
          current_responses: errorData.current_responses,
          max_responses: errorData.max_responses
        });
      } else if (error.response?.data?.detail?.error === 'client_not_found') {
        setResult(error.response.data.detail);
        setCurrentStep('client_not_found');
      } else {
        alert('Erro ao iniciar pesquisa. Tente novamente.');
      }
    }
    setLoading(false);
  };

  const saveDemographics = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/demographics`, {
        session_id: sessionId,
        ...demographics,
        tag
      });
      setCurrentStep('questions');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving demographics:', error);
      alert('Erro ao salvar seus dados. Verifique sua conexão e tente novamente.');
    }
    setLoading(false);
  };

  const saveResponses = async () => {
    setLoading(true);
    try {
      const responsesArray = [];
      Object.entries(responses).forEach(([questionId, score]) => {
        const dimension = findQuestionDimension(parseInt(questionId));
        responsesArray.push({
          session_id: sessionId,
          question_id: parseInt(questionId),
          dimension,
          score
        });
      });

      await axios.post(`${API}/responses`, responsesArray);
      
      const resultResponse = await axios.post(`${API}/complete?session_id=${sessionId}`);
      setResult(resultResponse.data);
      setCurrentStep('complete');
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving responses:', error);
      alert('Erro ao enviar suas respostas. Verifique sua conexão e tente novamente — suas respostas foram preservadas.');
    }
    setLoading(false);
  };

  const findQuestionDimension = (questionId) => {
    for (const [dimName, dimData] of Object.entries(questions)) {
      if (dimData.questions.some(q => q.id === questionId)) {
        return dimName;
      }
    }
    return null;
  };

  const getCurrentDimensionQuestions = () => {
    const dimName = dimensionNames[currentDimension];
    return questions[dimName]?.questions || [];
  };

  const allCurrentDimensionAnswered = () => {
    const currentQuestions = getCurrentDimensionQuestions();
    return currentQuestions.every(q => responses[q.id]);
  };

  const nextDimension = () => {
    if (currentDimension < dimensionNames.length - 1) {
      setCurrentDimension(currentDimension + 1);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      saveResponses();
    }
  };

  const prevDimension = () => {
    if (currentDimension > 0) {
      setCurrentDimension(currentDimension - 1);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const progress = ((currentDimension + 1) / dimensionNames.length) * 100;

  // Welcome, Demographics, Questions, and Complete steps with theme support...
  if (currentStep === 'welcome') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-2xl mx-auto text-center">
          <div className={`rounded-2xl shadow-2xl p-8 md:p-12 border relative ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              }`}
              title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            
            <img 
              src={isDarkMode ? "/logo.png" : "/logo-new.png"}
              alt="DEMoraes Consultoria" 
              className="w-72 mx-auto mb-8"
            />
            
            <h1 className={`text-3xl font-bold mb-6 leading-tight ${
              isDarkMode ? 'text-white' : 'text-slate-800'
            }`}>
              Pesquisa de Avaliação de Riscos Psicossociais no Trabalho
            </h1>
            
            <div className={`text-lg mb-8 space-y-4 leading-relaxed ${
              isDarkMode ? 'text-gray-300' : 'text-slate-600'
            }`}>
              <p><strong className={isDarkMode ? 'text-white' : 'text-slate-800'}>Olá, colaborador(a),</strong></p>
              <p>
                Este questionário identifica fatores da organização do trabalho que podem influenciar
                sua saúde e seu bem-estar. Ele integra o Gerenciamento de Riscos Ocupacionais da empresa
                e seu resultado orienta melhorias concretas nas condições de trabalho.
              </p>
              <p>
                São cerca de <strong className={isDarkMode ? 'text-white' : 'text-slate-800'}>10 minutos</strong> e
                não existem respostas certas ou erradas: o que interessa é a sua percepção sobre o dia a dia.
              </p>
              <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-slate-700'}`}>
                Responda com sinceridade — sua identidade não é registrada em momento algum.
              </p>
            </div>

            <p className={`text-xs mb-6 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              Ao iniciar o preenchimento, você concorda com a participação nesta avaliação. Os dados são
              tratados de forma anônima e analisados apenas de forma agregada, em conformidade com a Lei
              nº 13.709/2018 (LGPD). Grupos com menos de 5 respondentes não são detalhados nos relatórios,
              para que ninguém possa ser identificado. A participação é voluntária e você pode interromper
              a qualquer momento.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className={`p-6 rounded-xl border-2 hover:border-demoraes-green/40 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700/50 border-gray-600' 
                  : 'bg-demoraes-green/5 border-demoraes-green/20'
              }`}>
                <div className={`mb-3 ${isDarkMode ? 'text-gray-300' : 'text-demoraes-green'}`}>
                  <svg className="w-10 h-10 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h3 className={`font-bold mb-2 text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>🔒 Confidencial</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>Suas respostas são protegidas e seguras</p>
              </div>

              <div className={`p-6 rounded-xl border-2 hover:border-slate-300 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700/30 border-gray-600' 
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className={`mb-3 ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                  <svg className="w-10 h-10 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h3 className={`font-bold mb-2 text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>👤 Anônimo</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>Não identificamos respondentes</p>
              </div>

              <div className={`p-6 rounded-xl border-2 hover:border-demoraes-green/40 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700/50 border-gray-600' 
                  : 'bg-demoraes-green/5 border-demoraes-green/20'
              }`}>
                <div className={`mb-3 ${isDarkMode ? 'text-gray-300' : 'text-demoraes-green'}`}>
                  <svg className="w-10 h-10 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414L2.586 7l3.707-3.707a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h3 className={`font-bold mb-2 text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>📈 Impacto</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>Melhoria contínua do ambiente</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <button
                onClick={startSession}
                disabled={loading}
                className="bg-demoraes-green hover:bg-demoraes-green/90 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 hover:scale-105 active:scale-95"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Iniciando...
                  </div>
                ) : (
                  '🚀 INICIAR O PREENCHIMENTO'
                )}
              </button>
              
              <button
                onClick={() => window.location.href = '/admin'}
                className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-2 px-6 rounded-lg text-sm transition-all duration-200 hover:scale-105 active:scale-95"
              >
                🔐 Acesso Administrativo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'demographics') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-xl mx-auto">
          <div className={`rounded-2xl shadow-2xl p-8 border relative ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              }`}
              title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-demoraes-green rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                </svg>
              </div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Seu Perfil</h2>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                Estas informações permitem analisar os resultados por grupo e direcionar melhor as ações.
              </p>
              <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                Elas não identificam você: grupos com menos de 5 respondentes não são detalhados
                nos relatórios.
              </p>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-slate-700'}`}>🏢 Departamento</label>
                <select
                  value={demographics.department}
                  onChange={(e) => setDemographics({...demographics, department: e.target.value})}
                  className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-demoraes-green focus:border-demoraes-green transition-all duration-200 disabled:opacity-60 ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}
                  required
                  disabled={sectorsLoading}
                >
                  <option value="">
                    {sectorsLoading ? 'Carregando departamentos...' : 'Selecione seu departamento...'}
                  </option>
                  {(sectorOptions || DEFAULT_DEPARTMENTS).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {sectorsError && (
                  <p className={`mt-2 text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    Não foi possível carregar a lista personalizada de departamentos desta empresa; exibindo lista padrão.
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-slate-700'}`}>💼 Cargo/Função</label>
                <input
                  type="text"
                  value={demographics.position}
                  onChange={(e) => setDemographics({...demographics, position: e.target.value})}
                  className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-demoraes-green focus:border-demoraes-green transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600' 
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}
                  placeholder="Ex: Analista, Gerente, Técnico..."
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-slate-700'}`}>⏰ Tempo na Empresa</label>
                <select
                  value={demographics.company_time}
                  onChange={(e) => setDemographics({...demographics, company_time: e.target.value})}
                  className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-demoraes-green focus:border-demoraes-green transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600' 
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}
                  required
                >
                  <option value="">Selecione o tempo...</option>
                  <option value="menos-1-ano">📅 Menos de 1 ano</option>
                  <option value="1-3-anos">📅 1 a 3 anos</option>
                  <option value="3-5-anos">📅 3 a 5 anos</option>
                  <option value="5-10-anos">📅 5 a 10 anos</option>
                  <option value="mais-10-anos">📅 Mais de 10 anos</option>
                </select>
              </div>
            </div>

            <button
              onClick={saveDemographics}
              disabled={loading || !demographics.department || !demographics.position || !demographics.company_time}
              className="w-full mt-8 bg-demoraes-green hover:bg-demoraes-green/90 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 hover:scale-105 active:scale-95"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Salvando...
                </div>
              ) : (
                '➡️ Continuar para Perguntas'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'questions') {
    const currentQuestions = getCurrentDimensionQuestions();
    const currentDimName = dimensionNames[currentDimension];

    return (
      <div className={`min-h-screen p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-4xl mx-auto">
          {/* Theme toggle button */}
          <button
            onClick={toggleTheme}
            className={`fixed top-4 right-4 p-2 rounded-lg transition-colors z-10 ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
            }`}
            title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          
          <div className={`rounded-xl shadow-lg p-6 mb-6 border ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            <div className="flex justify-between items-center mb-3">
              <span className={`text-sm font-semibold ${
                isDarkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>Progresso da Pesquisa</span>
              <span className="text-sm font-bold text-demoraes-green">{Math.round(progress)}% Concluído</span>
            </div>
            <div className={`w-full rounded-full h-4 overflow-hidden ${
              isDarkMode ? 'bg-gray-600' : 'bg-slate-200'
            }`}>
              <div 
                className="bg-gradient-to-r from-demoraes-green to-green-400 h-4 rounded-full transition-all duration-500 ease-out shadow-inner"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className={`flex justify-between text-xs mt-2 ${
              isDarkMode ? 'text-gray-400' : 'text-slate-500'
            }`}>
              <span>Dimensão {currentDimension + 1} de {dimensionNames.length}</span>
              <span>{dimensionLabels[currentDimName]}</span>
            </div>
          </div>

          <div className={`rounded-2xl shadow-2xl p-8 border ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-demoraes-green rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">{currentDimension + 1}</span>
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${
                isDarkMode ? 'text-white' : 'text-slate-800'
              }`}>
                {dimensionLabels[currentDimName]}
              </h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                Indique o quanto cada afirmação corresponde à sua experiência no trabalho.
                Considere os últimos meses e escolha a opção mais próxima da sua percepção.
              </p>
            </div>

            <div className="space-y-8">
              {currentQuestions.map((question, index) => (
                <div key={question.id} className={`border-2 rounded-xl p-6 hover:border-demoraes-green/20 transition-all duration-200 ${
                  isDarkMode 
                    ? 'border-gray-600 bg-gray-700/30' 
                    : 'border-slate-100 bg-white'
                }`}>
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 bg-demoraes-green/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-sm font-bold text-demoraes-green">{index + 1}</span>
                    </div>
                    <p className={`text-lg font-medium leading-relaxed ${
                      isDarkMode ? 'text-white' : 'text-slate-800'
                    }`}>{question.text}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-6" role="radiogroup" aria-label={question.text}>
                    {getScaleForQuestion(question).map((option) => (
                      <label key={option.value} className="cursor-pointer">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={option.value}
                          checked={responses[question.id] === option.value}
                          onChange={() => setResponses({...responses, [question.id]: option.value})}
                          className="sr-only peer"
                        />
                        <div className={`p-4 border-2 rounded-xl transition-all duration-200 text-center hover:scale-105 peer-focus-visible:ring-2 peer-focus-visible:ring-demoraes-green peer-focus-visible:ring-offset-2 ${
                          responses[question.id] === option.value
                            ? `${option.borderColor} ${option.bgColor} ${option.textColor} shadow-lg scale-105`
                            : isDarkMode 
                              ? 'border-gray-600 hover:border-gray-500 bg-gray-700 text-gray-300'
                              : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600'
                        }`}>
                          <div className="text-2xl mb-2">{option.emoji}</div>
                          <div className="font-bold text-lg mb-1">{option.value}</div>
                          <div className="text-xs font-medium leading-tight">{option.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className={`flex justify-between items-center mt-10 pt-6 border-t ${
              isDarkMode ? 'border-gray-600' : 'border-slate-200'
            }`}>
              <button
                onClick={prevDimension}
                disabled={currentDimension === 0}
                className={`px-6 py-3 rounded-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95 font-semibold ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-slate-500 hover:bg-slate-600 text-white'
                }`}
              >
                ⬅️ Anterior
              </button>
              
              <div className="text-center">
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                  {getCurrentDimensionQuestions().filter(q => responses[q.id]).length} de {getCurrentDimensionQuestions().length} respondidas
                </div>
              </div>
              
              <button
                onClick={nextDimension}
                disabled={!allCurrentDimensionAnswered() || loading}
                className="px-6 py-3 bg-demoraes-green hover:bg-demoraes-green/90 text-white rounded-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95 font-semibold"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {currentDimension === dimensionNames.length - 1 ? 'Finalizando...' : 'Salvando...'}
                  </div>
                ) : (
                  currentDimension === dimensionNames.length - 1 ? '🏁 Finalizar Pesquisa' : 'Próxima ➡️'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'complete' && result) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-2xl mx-auto">
          <div className={`rounded-2xl shadow-2xl p-8 text-center border relative ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              }`}
              title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            
            <div className="text-demoraes-green mb-6">
              <svg className="w-20 h-20 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
            
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Respostas registradas. Obrigado!</h2>
            <p className={`mb-8 text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
              Sua participação foi concluída com sucesso. As respostas passam a integrar a avaliação
              dos fatores psicossociais da organização e orientam as medidas de melhoria das condições
              de trabalho.
            </p>

            <div className={`p-6 rounded-xl border mb-6 ${
              isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-50 border-slate-200'
            }`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>🔒 Garantias de Privacidade</h4>
              <div className={`grid md:grid-cols-3 gap-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                <div className="text-center">
                  <div className="text-demoraes-green mb-1">🔐</div>
                  <div className="font-medium">Confidencial</div>
                </div>
                <div className="text-center">
                  <div className="text-demoraes-green mb-1">👤</div>
                  <div className="font-medium">Anônimo</div>
                </div>
                <div className="text-center">
                  <div className="text-demoraes-green mb-1">🗄️</div>
                  <div className="font-medium">Seguro</div>
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-500 leading-relaxed">
              Seus dados são analisados apenas de forma agregada, junto às demais respostas. Grupos com
              menos de 5 respondentes não são detalhados nos relatórios, de modo que nenhuma resposta
              individual possa ser identificada.
            </p>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-400">
                Sistema desenvolvido pela <span className="font-semibold text-demoraes-green">DEMoraes Consultoria</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'limit_exceeded' && result) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-2xl mx-auto">
          <div className={`rounded-2xl shadow-2xl p-8 text-center border relative ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              }`}
              title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            
            <div className="text-red-500 mb-6">
              <svg className="w-20 h-20 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            </div>
            
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>⚠️ Limite Atingido</h2>
            <p className={`mb-6 text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
              {result.message}
            </p>

            <div className={`p-6 rounded-xl border mb-6 ${
              isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-red-50 border-red-200'
            }`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>📊 Informações do Cliente</h4>
              <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                <div><strong>Cliente:</strong> {result.client_name}</div>
                <div><strong>Respostas atuais:</strong> {result.current_responses}</div>
                <div><strong>Limite máximo:</strong> {result.max_responses}</div>
              </div>
            </div>

            <p className={`text-sm leading-relaxed mb-6 ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              Entre em contato com a DEMoraes Consultoria para aumentar o limite de respostas 
              ou aguarde a liberação de novas vagas.
            </p>

            <button
              onClick={() => window.location.reload()}
              className="bg-demoraes-green hover:bg-demoraes-green/90 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
            >
              🔄 Tentar Novamente
            </button>

            <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-600' : 'border-slate-200'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-400'}`}>
                Sistema desenvolvido pela <span className="font-semibold text-demoraes-green">DEMoraes Consultoria</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'client_not_found' && result) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-dark-charcoal via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      }`}>
        <div className="max-w-2xl mx-auto">
          <div className={`rounded-2xl shadow-2xl p-8 text-center border relative ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-slate-200'
          }`}>
            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              }`}
              title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            
            <div className="text-orange-500 mb-6">
              <svg className="w-20 h-20 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            </div>
            
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>🔍 Cliente Não Encontrado</h2>
            <p className={`mb-6 text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
              {result.message}
            </p>

            <div className={`p-6 rounded-xl border mb-6 ${
              isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-orange-50 border-orange-200'
            }`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>🏷️ Informações do Link</h4>
              <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                <div><strong>Tag informada:</strong> {result.tag}</div>
                <div><strong>Status:</strong> Link não encontrado ou inativo</div>
              </div>
            </div>

            <p className={`text-sm leading-relaxed mb-6 ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              Verifique se o link está correto ou entre em contato com a DEMoraes Consultoria 
              para obter um link válido.
            </p>

            <button
              onClick={() => window.location.reload()}
              className="bg-demoraes-green hover:bg-demoraes-green/90 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
            >
              🔄 Tentar Novamente
            </button>

            <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-600' : 'border-slate-200'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-400'}`}>
                Sistema desenvolvido pela <span className="font-semibold text-demoraes-green">DEMoraes Consultoria</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-demoraes-green"></div>
  </div>;
};
